import { Button } from '@components/common';
import { Pencil, Trash2 } from 'lucide-react';
import React from 'react';
import { AccountRecord } from '../../../../types/accounting';
import { formatAccountTypeLabel } from './accountForm';

interface AccountsTableProps {
  accounts: AccountRecord[];
  allAccounts: AccountRecord[];
  onEdit: (account: AccountRecord) => void;
  onDelete: (account: AccountRecord) => void;
}

function AccountStatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        isActive
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-gray-100 text-gray-600'
      }`}
    >
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

export function AccountsTable({
  accounts,
  allAccounts,
  onEdit,
  onDelete,
}: AccountsTableProps) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="max-h-[32rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Code
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Name
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Type
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Normal Balance
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Parent
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => {
              const parentAccount = allAccounts.find(
                (candidate) => candidate.id === account.parent_account_id,
              );

              return (
                <tr key={account.id} className="border-t border-gray-100">
                  <td className="px-5 py-4 font-medium text-gray-900">
                    {account.code}
                  </td>
                  <td className="px-5 py-4">
                    <div className="font-medium text-gray-900">
                      {account.name}
                    </div>
                    {account.subtype ? (
                      <div className="text-xs text-gray-500">
                        {account.subtype}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {formatAccountTypeLabel(account.type)}
                  </td>
                  <td className="px-5 py-4 capitalize text-gray-600">
                    {account.normal_balance}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {parentAccount
                      ? `${parentAccount.code} - ${parentAccount.name}`
                      : 'None'}
                  </td>
                  <td className="px-5 py-4">
                    <AccountStatusBadge isActive={account.is_active} />
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() => onEdit(account)}
                      >
                        <Pencil size={14} />
                        Edit
                      </Button>
                      {!account.is_system ? (
                        <Button
                          variant="text"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => onDelete(account)}
                        >
                          <Trash2 size={14} />
                          Delete
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
